import {
  cityPath,
  validateActivity,
  CEMETERY,
  CREMATORIUM,
  FUNERARIUM,
} from '../../constants/activities';
import {
  firstIndex,
  lastIndex,
  nextIndex,
  previousIndex,
} from '../../lib/combobox_navigation';

const DEBOUNCE_DELAY = 300;
const OPTION_SELECTOR = '.c-city-combobox-option';

const timers = new WeakMap();
const requests = new WeakMap();

const partsOf = input => {
  const root = input.closest('[data-city-combobox]');

  return {
    root,
    listbox: document.getElementById(input.getAttribute('aria-controls')),
    status: root.querySelector('[data-city-combobox-status]'),
    error: root.querySelector('[data-city-combobox-error]'),
    spinner: root.querySelector('[data-city-combobox-spinner]'),
  };
};

const optionPath = (option, activity) => {
  const { departmentCode, citySlug } = option.dataset;

  switch (validateActivity(activity)) {
    case FUNERARIUM:
      return `/funerariums${cityPath(departmentCode, citySlug)}`;
    case CREMATORIUM:
      return `/crematoriums/${departmentCode}`;
    case CEMETERY:
      return `${cityPath(departmentCode, citySlug)}#cimetieres`;
    default:
      return cityPath(departmentCode, citySlug);
  }
};

const options = listbox =>
  Array.from(listbox.querySelectorAll('[role="option"]'));

const activeOption = (input, listbox) => {
  const id = input.getAttribute('aria-activedescendant');

  return id ? listbox.querySelector(`#${CSS.escape(id)}`) : null;
};

const setActive = (input, option) => {
  const { listbox } = partsOf(input);

  options(listbox).forEach(other => {
    if (other === option) {
      other.setAttribute('aria-selected', 'true');
    } else {
      other.removeAttribute('aria-selected');
    }
  });

  if (option) {
    input.setAttribute('aria-activedescendant', option.id);
    // Not 'auto': the page sets scroll-behavior: smooth, and a held arrow key
    // would then stack one animation per keypress.
    option.scrollIntoView({ behavior: 'instant', block: 'nearest' });
  } else {
    input.removeAttribute('aria-activedescendant');
  }
};

const setExpanded = (input, expanded) => {
  const { listbox } = partsOf(input);

  input.setAttribute('aria-expanded', String(expanded));
  listbox.hidden = !expanded;

  if (!expanded) {
    setActive(input, null);
  }
};

const setBusy = (input, busy) => {
  const { spinner } = partsOf(input);

  input.setAttribute('aria-busy', String(busy));
  spinner.hidden = !busy;
};

const setStatus = (status, message, { visible = false } = {}) => {
  status.textContent = message;
  status.classList.toggle('sr-only', !visible);
  status.classList.toggle('c-city-combobox-empty', visible);
};

const clearResults = input => {
  const { listbox, status, error } = partsOf(input);

  setExpanded(input, false);
  listbox.innerHTML = '';
  setStatus(status, '');
  error.textContent = '';
};

const buildOption = (hit, index, listboxId) => {
  const option = document.createElement('li');

  option.id = `${listboxId}-option-${index}`;
  option.className = 'c-city-combobox-option';
  option.setAttribute('role', 'option');
  option.textContent = hit.postal_codes
    ? `${hit.name} (${hit.postal_codes})`
    : hit.name;
  option.dataset.cityName = hit.name;
  option.dataset.citySlug = hit.slug || '';
  option.dataset.departmentCode = hit.department_code?.toLowerCase() || '';

  return option;
};

const render = (input, hits) => {
  const { listbox, status, error } = partsOf(input);

  error.textContent = '';
  listbox.innerHTML = '';
  hits.forEach((hit, index) =>
    listbox.appendChild(buildOption(hit, index, listbox.id)),
  );

  const empty = hits.length === 0;

  setStatus(
    status,
    empty
      ? 'Aucune ville trouvée'
      : `${hits.length} résultat${hits.length > 1 ? 's' : ''} disponible${hits.length > 1 ? 's' : ''}`,
    { visible: empty },
  );

  setActive(input, null);
  setExpanded(input, hits.length > 0);
};

const fail = (input, err, query) => {
  const { error } = partsOf(input);

  clearResults(input);
  error.textContent = 'Une erreur est survenue. Veuillez réessayer.';

  if (window.Sentry) {
    window.Sentry.captureException(err, { extra: { query } });
  }
};

const search = (input, query) => {
  requests.get(input)?.abort();

  const controller = new AbortController();
  requests.set(input, controller);
  setBusy(input, true);

  fetch(`/external_api/v1/cities/search?q=${encodeURIComponent(query)}`, {
    signal: controller.signal,
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`City search failed: ${response.status}`);
      }

      return response.json();
    })
    .then(hits => {
      setBusy(input, false);
      render(input, hits);
    })
    .catch(err => {
      if (err.name === 'AbortError') {
        return;
      }

      setBusy(input, false);
      fail(input, err, query);
    });
};

const selectOption = (input, option) => {
  input.value = option.dataset.cityName;
  clearResults(input);

  input.dispatchEvent(
    new CustomEvent('city-combobox:select', {
      bubbles: true,
      detail: {
        name: option.dataset.cityName,
        slug: option.dataset.citySlug || null,
        departmentCode: option.dataset.departmentCode || null,
      },
    }),
  );

  if (input.dataset.onSelect === 'navigate' && option.dataset.departmentCode) {
    setBusy(input, true);
    window.location.href = optionPath(option, input.dataset.activityType);
  }
};

const moveActive = (input, resolveIndex) => {
  const { listbox } = partsOf(input);
  const all = options(listbox);

  if (all.length === 0) {
    return;
  }

  if (listbox.hidden) {
    setExpanded(input, true);
  }

  const current = activeOption(input, listbox);
  const from = current ? all.indexOf(current) : -1;

  setActive(input, all[resolveIndex(from, all.length)]);
};

const onInput = input => {
  const query = input.value.trim();

  clearTimeout(timers.get(input));
  requests.get(input)?.abort();

  if (query === '') {
    setBusy(input, false);
    clearResults(input);
    input.dispatchEvent(
      new CustomEvent('city-combobox:clear', { bubbles: true }),
    );

    return;
  }

  timers.set(
    input,
    setTimeout(() => search(input, query), DEBOUNCE_DELAY),
  );
};

const onKeydown = (input, event) => {
  const { listbox } = partsOf(input);

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      moveActive(input, nextIndex);
      break;
    case 'ArrowUp':
      event.preventDefault();
      moveActive(input, previousIndex);
      break;
    case 'Home':
      if (!listbox.hidden) {
        event.preventDefault();
        moveActive(input, firstIndex);
      }
      break;
    case 'End':
      if (!listbox.hidden) {
        event.preventDefault();
        moveActive(input, lastIndex);
      }
      break;
    case 'Enter': {
      const option = activeOption(input, listbox);

      if (option) {
        event.preventDefault();
        selectOption(input, option);
      }
      break;
    }
    case 'Escape':
      // Closed listbox: let the Escape reach an enclosing dialog.
      if (!listbox.hidden) {
        event.preventDefault();
        event.stopPropagation();
        setExpanded(input, false);
      }
      break;
    case 'Tab':
      setExpanded(input, false);
      break;
    default:
      break;
  }
};

if (window.cityComboboxInitialized === undefined) {
  window.cityComboboxInitialized = true;

  document.addEventListener('input', event => {
    if (event.target.matches('[data-city-combobox-input]')) {
      onInput(event.target);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.target.matches('[data-city-combobox-input]')) {
      onKeydown(event.target, event);
    }
  });

  // mousedown keeps the focus on the input, so the blur never hides the listbox
  // before the click; click is the fallback for activations without mousedown.
  const selectFromEvent = event => {
    const option = event.target.closest(OPTION_SELECTOR);
    const root = option?.closest('[data-city-combobox]');

    if (!root) {
      return false;
    }

    event.preventDefault();
    selectOption(root.querySelector('[data-city-combobox-input]'), option);

    return true;
  };

  document.addEventListener('mousedown', event => {
    if (selectFromEvent(event)) {
      return;
    }

    document.querySelectorAll('[data-city-combobox]').forEach(root => {
      if (!root.contains(event.target)) {
        setExpanded(root.querySelector('[data-city-combobox-input]'), false);
      }
    });
  });

  document.addEventListener('click', selectFromEvent);

  document.addEventListener('focusout', event => {
    if (!event.target.matches('[data-city-combobox-input]')) {
      return;
    }

    const root = event.target.closest('[data-city-combobox]');

    if (!root.contains(event.relatedTarget)) {
      setExpanded(event.target, false);
    }
  });
}
